import React, { useState, useMemo } from 'react'
import {
  Typography,
  withStyles,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import {
  CoreTextField,
  CoreButton,
  CoreAutoComplete,
  CoreToggleButtonGroup,
  CoreToggleButton,
} from 'src/components/core'
import { messageBoxHideAction } from 'src/reducers/message-box/action'

import styles from './styles'

const ALL_SECTIONS = { name: 'All sections', value: 'allsections' }

const AiActivityPopup = props => {
  const { classes, activityType, languageCode, onSubmit } = props
  const dispatch = useDispatch()

  const sections = useSelector(state => state.course.course?.sections || [])
  const currentCardId = useSelector(state => state.card.currentCard?.id)

  const sectionOptions = useMemo(
    () => [
      ALL_SECTIONS,
      ...sections.map(s => ({ name: s.name, value: s.id })),
    ],
    [sections],
  )

  const [resourceType, setResourceType] = useState('course')
  const [existingMode, setExistingMode] = useState(() =>
    currentCardId ? 'page' : 'section',
  )
  const [selectedSection, setSelectedSection] = useState(ALL_SECTIONS)
  const [description, setDescription] = useState('')

  const handleResourceTypeChange = (_event, value) => {
    if (value !== null) setResourceType(value)
  }

  const handleSectionChange = (_event, value) => {
    setSelectedSection(value || ALL_SECTIONS)
  }

  const handleBack = () => {
    dispatch(messageBoxHideAction('message-box-1'))
  }

  const isCourseMode = resourceType === 'course'
  const isPageExistingMode = isCourseMode && existingMode === 'page'
  const isSectionExistingMode = isCourseMode && existingMode === 'section'
  const trimmedDescription = description.trim()

  const isDisabled = isCourseMode
    ? isPageExistingMode
      ? !currentCardId
      : !selectedSection
    : trimmedDescription.length === 0

  const handleGenerate = () => {
    if (isDisabled) return

    const language = languageCode || 'en'
    let payload

    if (isPageExistingMode) {
      payload = {
        type: activityType,
        pageId: currentCardId,
        language,
      }
    } else if (isSectionExistingMode) {
      const sectionIds =
        selectedSection.value === ALL_SECTIONS.value
          ? sections.map(s => s.id)
          : [selectedSection.value]

      payload = {
        type: activityType,
        sectionIds,
        language,
      }
    } else {
      payload = {
        type: activityType,
        content: trimmedDescription,
        language,
      }
    }

    onSubmit?.(payload)
  }

  return (
    <div>
      <div className={classes.rootContainer}>
        <CoreToggleButtonGroup
          value={resourceType}
          onChange={handleResourceTypeChange}
          exclusive
          fullWidth
          label="SELECT SOURCE"
        >
          <CoreToggleButton value="course">
            <Icon icon="mynaui:book-open" />
            <Typography className={classes.header}>
              Use existing content
            </Typography>
          </CoreToggleButton>
          <CoreToggleButton value="ai">
            <Icon icon="hugeicons:ai-book" />
            <Typography className={classes.header}>
              AI knowledge base
            </Typography>
          </CoreToggleButton>
        </CoreToggleButtonGroup>

        {isCourseMode ? (
          <div>
            <RadioGroup
              value={existingMode}
              onChange={e => setExistingMode(e.target.value)}
            >
              <FormControlLabel
                value="page"
                control={<Radio size="small" color="primary" />}
                label="Current page"
                disabled={!currentCardId}
              />
              <FormControlLabel
                value="section"
                control={<Radio size="small" color="primary" />}
                label="Section"
              />
            </RadioGroup>
            <CoreAutoComplete
              marginTop={true}
              fullWidth
              label="Select section AI will extract content from"
              defaultValue={ALL_SECTIONS}
              value={selectedSection}
              selectOnFocus
              options={sectionOptions}
              getOptionLabel={option => option.name || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              onChange={handleSectionChange}
              freeSolo={false}
              disabled={!isSectionExistingMode}
            />
          </div>
        ) : (
          <CoreTextField
            id="activity-description-input"
            autoFocus
            multiline
            value={description}
            onChange={e => setDescription(e.target.value)}
            variant="outlined"
            fullWidth
            label="Description"
            placeholder="Describe the topic or concept you want to generate the activity from."
            marginTop={true}
          />
        )}
      </div>

      <div className={classes.buttonContainer}>
        <CoreButton variant="text" color="secondary" onClick={handleBack}>
          Back
        </CoreButton>
        <CoreButton
          variant="contained"
          color="primary"
          className={classes.addButton}
          onClick={handleGenerate}
          disabled={isDisabled}
        >
          Generate
        </CoreButton>
      </div>
    </div>
  )
}

export default withStyles(styles)(AiActivityPopup)
